import {
  Box,
  Button,
  Card,
  DateInput,
  RadioGroup,
  SelectInput,
  TextInput,
  TextareaInput,
  toast,
} from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  ActionOnAllocationAccountsResponse,
  AllocationTermOptions,
  IncentiveAssignmentStatus,
  RECIncentiveType,
  RECIncentiveTypeLabels,
  RECMonetizationMethodLabels,
} from 'types';

import {
  useAssignAccountsToAllocationMutation,
  useCreateAllocationMutation,
  useGetIncentiveQuery,
} from '../../services/incentives';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { ActionAccountsResult } from './ActionAccountsResult';

const recTypeOptions = Object.entries(RECIncentiveTypeLabels).map(([value, label]) => ({ value, label }));

const monetizationMethodOptions = Object.entries(RECMonetizationMethodLabels).map(([value, label]) => ({
  value,
  label,
}));

const termOptions = AllocationTermOptions.map((opt) => ({ value: opt.value, label: opt.label }));

const validationSchema = yup.object({
  allocationMode: yup.string().required(),
  existingAllocationId: yup.string().when('allocationMode', {
    is: 'existing',
    then: (schema) => schema.required('Please select an allocation'),
    otherwise: (schema) => schema.notRequired(),
  }),
  newAllocationName: yup.string().when('allocationMode', {
    is: 'new',
    then: (schema) => schema.required('Allocation name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  allocationDate: yup
    .date()
    .nullable()
    .when('allocationMode', {
      is: 'new',
      then: (schema) => schema.required('Allocation date is required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  term: yup.string().when('allocationMode', {
    is: 'new',
    then: (schema) => schema.required('Term is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  recType: yup.string().required('REC Type is required'),
  accountIds: yup.string().required('Please enter at least one account ID'),
});

export const AllocateAccounts = () => {
  const { key } = useParams() as { key: string };
  const navigate = useNavigate();
  const { data: incentive, isFetching } = useGetIncentiveQuery({ key });
  const [createAllocation] = useCreateAllocationMutation();
  const [assignAccounts] = useAssignAccountsToAllocationMutation();
  const [result, setResult] = useState<ActionOnAllocationAccountsResponse | null>(null);

  const existingAllocations = incentive?.allocations || [];
  const allocationOptions = existingAllocations.map((a) => ({ value: a.id, label: a.name }));

  const formik = useFormik({
    initialValues: {
      allocationMode: existingAllocations.length > 0 ? 'existing' : 'new',
      existingAllocationId: '',
      newAllocationName: '',
      allocationDate: null,
      monetizationMethod: '',
      recOwner: 'Palmetto',
      partnerOrBuyer: '',
      term: '',
      recType: RECIncentiveType.contracted,
      accountIds: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: FormikValues, { setSubmitting }) => {
      try {
        const accountIdList = values.accountIds
          .split(/[\n,]+/)
          .map((id: string) => id.trim())
          .filter((id: string) => id.length > 0);

        if (accountIdList.length === 0) {
          toast.error('Please enter at least one valid account ID');
          setSubmitting(false);
          return;
        }

        let assignmentResult: ActionOnAllocationAccountsResponse;

        if (values.allocationMode === 'new') {
          assignmentResult = await createAllocation({
            key,
            data: {
              name: values.newAllocationName,
              allocationDate: values.allocationDate?.toISOString() || '',
              monetizationMethod: values.monetizationMethod || undefined,
              recOwner: values.recOwner || undefined,
              partnerOrBuyer: values.partnerOrBuyer || undefined,
              term: values.term,
              accountIds: accountIdList,
              recType: values.recType,
            },
          }).unwrap();
        } else {
          assignmentResult = await assignAccounts({
            key,
            allocationId: values.existingAllocationId,
            accountIds: accountIdList,
            recType: values.recType,
          }).unwrap();
        }

        setResult(assignmentResult);

        if (assignmentResult.actionedAccounts.length > 0) {
          toast.success(`Successfully assigned ${assignmentResult.actionedAccounts.length} account(s)`);
        }

        if (assignmentResult.invalidAccounts.length > 0 || assignmentResult.accountsAlreadyActioned.length > 0) {
          toast.error('Some accounts could not be assigned. See details below.');
        }
      } catch (error: any) {
        console.error('Error allocating accounts:', error);
        toast.error(error?.data?.message || 'Error allocating accounts');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleBack = () => {
    navigate(`/admin/incentives/${key}`);
  };

  if (result) {
    return (
      <ActionAccountsResult
        result={result}
        action={IncentiveAssignmentStatus.active}
        onAllocateMore={() => {
          setResult(null);
          formik.resetForm();
        }}
        onBack={handleBack}
        incentiveKey={key}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Allocate Accounts - {incentive?.name ?? 'Incentive'}</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          eyebrow={<Link to={`/admin/incentives/${key}`}>{incentive?.name || 'Incentive'}</Link>}
          title="Allocate Accounts"
          description="Assign accounts to an allocation for this incentive"
        />

        <Box padding="lg 0">
          <form onSubmit={formik.handleSubmit}>
            <Box direction="column" childGap="lg" maxWidth="800px">
              <Card padding="lg" overflow="visible">
                <Box as="h3" fontWeight="medium" fontSize="md" margin="0 0 md 0">
                  Allocation
                </Box>

                {existingAllocations.length > 0 && (
                  <Box margin="0 0 lg 0">
                    <RadioGroup
                      name="allocationMode"
                      value={formik.values.allocationMode}
                      onChange={(event) => formik.setFieldValue('allocationMode', event.target.value)}
                      options={[
                        { id: 'existing', value: 'existing', label: 'Use Existing Allocation' },
                        { id: 'new', value: 'new', label: 'Create New Allocation' },
                      ]}
                    />
                  </Box>
                )}

                {formik.values.allocationMode === 'existing' && existingAllocations.length > 0 ? (
                  <SelectInput
                    label="Select Allocation"
                    name="existingAllocationId"
                    id="existingAllocationId"
                    value={allocationOptions.find((opt) => opt.value === formik.values.existingAllocationId) || null}
                    onChange={(event) =>
                      formik.setFieldValue('existingAllocationId', event.target.value?.value || '')
                    }
                    onBlur={formik.handleBlur}
                    error={formik.touched.existingAllocationId && (formik.errors.existingAllocationId as string)}
                    options={allocationOptions}
                    isRequired
                    isDisabled={isFetching}
                  />
                ) : (
                  <Box direction="column" childGap="md">
                    <TextInput
                      type="text"
                      label="Allocation Name"
                      name="newAllocationName"
                      id="newAllocationName"
                      value={formik.values.newAllocationName}
                      onChange={(event) => formik.setFieldValue('newAllocationName', event.target.value)}
                      onBlur={formik.handleBlur}
                      error={formik.touched.newAllocationName && (formik.errors.newAllocationName as string)}
                      isRequired
                    />

                    <DateInput
                      id="allocationDate"
                      name="allocationDate"
                      label="Allocation Date"
                      placeholder="MM-DD-YYYY"
                      value={
                        formik.values.allocationDate ? formik.values.allocationDate.toLocaleDateString('en-US') : ''
                      }
                      error={formik.touched.allocationDate && (formik.errors.allocationDate as string)}
                      isRequired
                      datePickerProps={{
                        onChange: (date) => formik.setFieldValue('allocationDate', date),
                        selected: formik.values.allocationDate || undefined,
                      }}
                      textInputProps={{}}
                    />

                    <SelectInput
                      label="Term"
                      name="term"
                      id="term"
                      value={termOptions.find((opt) => opt.value === formik.values.term) || null}
                      onChange={(event) => formik.setFieldValue('term', event.target.value?.value)}
                      onBlur={formik.handleBlur}
                      error={formik.touched.term && (formik.errors.term as string)}
                      options={termOptions}
                      isRequired
                    />

                    <SelectInput
                      label="Monetization Method"
                      name="monetizationMethod"
                      id="monetizationMethod"
                      value={
                        monetizationMethodOptions.find((opt) => opt.value === formik.values.monetizationMethod) ||
                        null
                      }
                      onChange={(event) =>
                        formik.setFieldValue('monetizationMethod', event.target.value?.value || '')
                      }
                      onBlur={formik.handleBlur}
                      options={monetizationMethodOptions}
                      isClearable
                    />

                    <Box direction="row" childGap="md" alignItems="flex-end">
                      <Box flex="auto">
                        <TextInput
                          type="text"
                          label="REC Owner"
                          name="recOwner"
                          id="recOwner"
                          value={formik.values.recOwner}
                          onChange={(event) => formik.setFieldValue('recOwner', event.target.value)}
                          onBlur={formik.handleBlur}
                          helpText="Defaults to 'Palmetto'"
                        />
                      </Box>
                      <Box flex="auto">
                        <TextInput
                          type="text"
                          label="Partner or Buyer"
                          name="partnerOrBuyer"
                          id="partnerOrBuyer"
                          value={formik.values.partnerOrBuyer}
                          onChange={(event) => formik.setFieldValue('partnerOrBuyer', event.target.value)}
                          onBlur={formik.handleBlur}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Card>

              <Card padding="lg">
                <Box as="h3" fontWeight="medium" fontSize="md" margin="0 0 md 0">
                  Accounts
                </Box>
                <Box margin="0 0 md 0">
                  <SelectInput
                    label="REC Type"
                    name="recType"
                    id="recType"
                    value={recTypeOptions.find((opt) => opt.value === formik.values.recType) || null}
                    onChange={(event) => formik.setFieldValue('recType', event.target.value?.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.recType && (formik.errors.recType as string)}
                    options={recTypeOptions}
                    isRequired
                  />
                </Box>
                <TextareaInput
                  label="Account IDs"
                  name="accountIds"
                  id="accountIds"
                  value={formik.values.accountIds}
                  onChange={(event) => formik.setFieldValue('accountIds', event.target.value)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.accountIds && (formik.errors.accountIds as string)}
                  rows={6}
                  helpText="Enter account IDs separated by commas or new lines"
                  isRequired
                />
              </Card>

              <Box direction="row" childGap="md" justifyContent="flex-end">
                <Button variant="secondary" tone="neutral" size="md" onClick={handleBack}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
                  Allocate Accounts
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </MainContainer>
    </>
  );
};
